import React, { useState } from 'react';
import Modal from 'react-bootstrap/Modal';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './AssessmentModal.css';

const TOTAL_STEPS = 6;

const initialFormData = {
  // Step 1 — Personal Details
  fullName: '',
  email: '',
  phoneNumber: '',
  countryOfResidence: '',
  // Step 2 — About You
  age: '',
  heightCm: '',
  weightKg: '',
  // Step 3 — Your Situation
  diagnosis: '',
  cancerStage: '',
  diagnosisDate: '',
  currentTreatment: '',
  // Step 4 — How You're Feeling
  feelingDescription: '',
  energyLevel: '',
  dailyActivity: '',
  // Step 5 — What Matters Most
  improvementGoal: '',
  expectationsFromDoctor: '',
  personalHopes: '',
  // Step 6 — Final Step
  additionalNotes: '',
};

export default function AssessmentModal(props) {
  const [step, setStep] = useState(0);
  const [formData, setFormData] = useState(initialFormData);
  const [errors, setErrors] = useState({});
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  /* ── Handlers ─────────────────────────────────────────── */

  const handleClose = () => {
    if (step > 0 && step < 7) {
      setShowCloseConfirm(true);
    } else {
      resetAndClose();
    }
  };

  const resetAndClose = () => {
    setStep(0);
    setFormData(initialFormData);
    setErrors({});
    setShowCloseConfirm(false);
    props.onHide();
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const handleSelect = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: '' }));
  };

  /* ── Validation ───────────────────────────────────────── */

  const validateStep = () => {
    const errs = {};

    if (step === 1) {
      if (!formData.fullName.trim()) errs.fullName = 'Full name is required';
      if (!formData.email.trim()) errs.email = 'Email is required';
      else if (!/\S+@\S+\.\S+/.test(formData.email)) errs.email = 'Invalid email format';
      if (!formData.phoneNumber.trim()) errs.phoneNumber = 'Phone number is required';
      if (!formData.countryOfResidence.trim()) errs.countryOfResidence = 'Country is required';
    }

    if (step === 2) {
      if (!formData.age) {
        errs.age = 'Age is required';
      } else if (isNaN(formData.age) || Number(formData.age) < 1 || Number(formData.age) > 120) {
        errs.age = 'Please enter a valid age';
      }
    }

    if (step === 3) {
      if (!formData.diagnosis.trim()) errs.diagnosis = 'Please describe your diagnosis';
    }

    if (step === 4) {
      if (!formData.energyLevel) errs.energyLevel = 'Please select your energy level';
      if (!formData.dailyActivity) errs.dailyActivity = 'Please select your daily activity level';
    }

    if (step === 5) {
      if (!formData.improvementGoal.trim()) errs.improvementGoal = "Please tell us what you'd like to improve";
      if (!formData.personalHopes.trim()) errs.personalHopes = "Please share what you're hoping to achieve";
    }

    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleNext = () => {
    if (validateStep()) setStep((prev) => prev + 1);
  };

  const handleBack = () => {
    setStep((prev) => prev - 1);
    setErrors({});
  };

  const handleSubmit = async () => {
    if (!validateStep()) return;
    setSubmitting(true);
    try {
      const response = await fetch('/api/Assessment/Submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          fullName: formData.fullName,
          email: formData.email,
          phoneNumber: formData.phoneNumber,
          countryOfResidence: formData.countryOfResidence,
          age: formData.age ? parseInt(formData.age, 10) : null,
          heightCm: formData.heightCm ? parseFloat(formData.heightCm) : null,
          weightKg: formData.weightKg ? parseFloat(formData.weightKg) : null,
          diagnosis: formData.diagnosis,
          cancerStage: formData.cancerStage || null,
          diagnosisDate: formData.diagnosisDate || null,
          currentTreatment: formData.currentTreatment || null,
          feelingDescription: formData.feelingDescription || null,
          energyLevel: formData.energyLevel,
          dailyActivity: formData.dailyActivity,
          improvementGoal: formData.improvementGoal,
          expectationsFromDoctor: formData.expectationsFromDoctor || null,
          personalHopes: formData.personalHopes,
          additionalNotes: formData.additionalNotes || null,
        }),
      });
      if (response.ok) {
        setStep(7);
      } else {
        toast.error('Something went wrong. Please try again.');
      }
    } catch {
      toast.error('Unable to submit. Please check your connection and try again.');
    } finally {
      setSubmitting(false);
    }
  };

  /* ── Progress bar ─────────────────────────────────────── */

  const progressPercent =
    step >= 1 && step <= TOTAL_STEPS ? ((step - 1) / (TOTAL_STEPS - 1)) * 100 : 0;

  const renderProgress = () =>
    step >= 1 && step <= TOTAL_STEPS ? (
      <div className="progress-wrapper" style={{ flex: 1, paddingRight: 10 }}>
        <div className="assessment-progress-bar">
          <div className="assessment-progress-fill" style={{ width: `${progressPercent}%` }} />
        </div>
        <p className="assessment-step-label">
          Step {step} of {TOTAL_STEPS}
        </p>
      </div>
    ) : null;

  /* ── Step renderers ───────────────────────────────────── */

  const renderStep = () => {
    switch (step) {
      /* ── Intro ── */
      case 0:
        return (
          <div className="assessment-intro">
            <p className="assessment-eyebrow">Cancer Management</p>
            <h2>Confidential Assessment</h2>
            <p>This process allows us to understand your situation in detail.</p>
            <p>Based on this, we determine whether a private consultation is appropriate.</p>
            <p style={{ marginBottom: 32 }}>All information is handled with strict confidentiality.</p>
            <button className="assessment-nav-btn" onClick={() => setStep(1)}>
              Begin Assessment
            </button>
          </div>
        );

      /* ── Step 1: Personal Details ── */
      case 1:
        return (
          <>
            <h4 className="assessment-title">Personal Details</h4>

            <div className="mb-3">
              <label className="form-label fw-semibold">Full Name</label>
              <input
                type="text"
                className={`form-control ${errors.fullName ? 'is-invalid' : ''}`}
                name="fullName"
                placeholder="Your full name"
                value={formData.fullName}
                onChange={handleChange}
              />
              {errors.fullName && <div className="invalid-feedback">{errors.fullName}</div>}
            </div>

            <div className="mb-3">
              <label className="form-label fw-semibold">Email Address</label>
              <input
                type="email"
                className={`form-control ${errors.email ? 'is-invalid' : ''}`}
                name="email"
                placeholder="you@example.com"
                value={formData.email}
                onChange={handleChange}
              />
              {errors.email && <div className="invalid-feedback">{errors.email}</div>}
            </div>

            <div className="mb-3">
              <label className="form-label fw-semibold">Phone Number</label>
              <input
                type="tel"
                className={`form-control ${errors.phoneNumber ? 'is-invalid' : ''}`}
                name="phoneNumber"
                placeholder="+1 (555) 123-4567"
                value={formData.phoneNumber}
                onChange={handleChange}
              />
              {errors.phoneNumber && <div className="invalid-feedback">{errors.phoneNumber}</div>}
            </div>

            <div className="mb-3">
              <label className="form-label fw-semibold">Country of Residence</label>
              <input
                type="text"
                className={`form-control ${errors.countryOfResidence ? 'is-invalid' : ''}`}
                name="countryOfResidence"
                placeholder="Your country"
                value={formData.countryOfResidence}
                onChange={handleChange}
              />
              {errors.countryOfResidence && (
                <div className="invalid-feedback">{errors.countryOfResidence}</div>
              )}
            </div>
          </>
        );

      /* ── Step 2: About You ── */
      case 2:
        return (
          <>
            <h4 className="assessment-title">About You</h4>

            <div className="assessment-section-label">Basic Information</div>
            <div className="mb-3">
              <label className="form-label fw-semibold">Age (years)</label>
              <input
                type="number"
                className={`form-control ${errors.age ? 'is-invalid' : ''}`}
                name="age"
                placeholder="e.g. 45"
                value={formData.age}
                onChange={handleChange}
              />
              {errors.age && <div className="invalid-feedback">{errors.age}</div>}
            </div>

            <div className="assessment-section-label">Body Metrics</div>
            <div className="row mb-2">
              <div className="col-6">
                <label className="form-label fw-semibold">Height (cm)</label>
                <input
                  type="number"
                  className="form-control"
                  name="heightCm"
                  placeholder="e.g. 180"
                  value={formData.heightCm}
                  onChange={handleChange}
                />
              </div>
              <div className="col-6">
                <label className="form-label fw-semibold">Weight (kg)</label>
                <input
                  type="number"
                  className="form-control"
                  name="weightKg"
                  placeholder="e.g. 75"
                  value={formData.weightKg}
                  onChange={handleChange}
                />
              </div>
            </div>
            <div className="text-end">
              <small className="text-muted">
                Takes less than <strong>1 minute</strong>
              </small>
            </div>
          </>
        );

      /* ── Step 3: Your Situation ── */
      case 3:
        return (
          <>
            <h4 className="assessment-title">Your Situation</h4>
            <p className="text-muted" style={{ fontSize: 14, marginBottom: 20 }}>
              This helps us better understand your situation before a consultation.
            </p>

            <div className="mb-3">
              <label className="form-label fw-semibold">What have you been diagnosed with?</label>
              <small className="d-block text-muted mb-1">You can write this in your own words</small>
              <input
                type="text"
                className={`form-control ${errors.diagnosis ? 'is-invalid' : ''}`}
                name="diagnosis"
                placeholder="Please specify in your own words"
                value={formData.diagnosis}
                onChange={handleChange}
              />
              {errors.diagnosis && <div className="invalid-feedback">{errors.diagnosis}</div>}
            </div>

            <div className="row mb-3">
              <div className="col-6">
                <label className="form-label fw-semibold">
                  Do you know the stage?{' '}
                  <span className="text-muted fw-normal">(optional)</span>
                </label>
                <select
                  className="form-control"
                  name="cancerStage"
                  value={formData.cancerStage}
                  onChange={handleChange}
                >
                  <option value="">Select stage</option>
                  <option value="Stage I">Stage I</option>
                  <option value="Stage II">Stage II</option>
                  <option value="Stage III">Stage III</option>
                  <option value="Stage IV">Stage IV</option>
                  <option value="Unknown">Unknown</option>
                </select>
              </div>
              <div className="col-6">
                <label className="form-label fw-semibold">When were you diagnosed?</label>
                <input
                  type="text"
                  className="form-control"
                  name="diagnosisDate"
                  placeholder="e.g. March 2023"
                  value={formData.diagnosisDate}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div className="mb-3">
              <label className="form-label fw-semibold">
                Are you currently receiving any treatment?{' '}
                <span className="text-muted fw-normal">Briefly describe if yes</span>
              </label>
              <textarea
                className="form-control"
                name="currentTreatment"
                placeholder="Please describe any ongoing treatments"
                rows={3}
                value={formData.currentTreatment}
                onChange={handleChange}
              />
            </div>
          </>
        );

      /* ── Step 4: How You're Feeling ── */
      case 4:
        return (
          <>
            <h4 className="assessment-title">How You're Feeling</h4>
            <p className="text-muted" style={{ fontSize: 14, marginBottom: 20 }}>
              Help us understand how you're feeling day to day.
            </p>

            <div className="mb-3">
              <label className="form-label fw-semibold">Describe how you're feeling</label>
              <textarea
                className="form-control"
                name="feelingDescription"
                placeholder="Share any symptoms, limitations, or relevant details"
                rows={3}
                value={formData.feelingDescription}
                onChange={handleChange}
              />
            </div>

            <div className="mb-3">
              <label className={`form-label fw-semibold ${errors.energyLevel ? 'text-danger' : ''}`}>
                Energy level
                {errors.energyLevel && (
                  <span className="fw-normal ms-1">— {errors.energyLevel}</span>
                )}
              </label>
              <div className="row g-2 align-items-stretch">
                {[
                  { value: 'Low', label: 'Low', sub: 'Often tired, limited energy' },
                  { value: 'Moderate', label: 'Moderate', sub: 'Some good days, some low energy' },
                  { value: 'High', label: 'High', sub: 'Mostly steady energy' },
                ].map((opt) => (
                  <div className="col-4 d-flex" key={opt.value}>
                    <button
                      type="button"
                      className={`assessment-option-btn w-100 ${
                        formData.energyLevel === opt.value ? 'selected' : ''
                      }`}
                      onClick={() => handleSelect('energyLevel', opt.value)}
                    >
                      <div className="fw-semibold">
                        {opt.label}
                        {formData.energyLevel === opt.value && (
                          <span className="assessment-option-check"> ✓</span>
                        )}
                      </div>
                      <div style={{ fontSize: 11, color: '#777', marginTop: 3 }}>{opt.sub}</div>
                    </button>
                  </div>
                ))}
              </div>
            </div>

            <div className="mb-3">
              <label className={`form-label fw-semibold ${errors.dailyActivity ? 'text-danger' : ''}`}>
                Daily activity
                {errors.dailyActivity && (
                  <span className="fw-normal ms-1">— {errors.dailyActivity}</span>
                )}
              </label>
              <div className="row g-2 align-items-stretch">
                {[
                  { value: 'Not very active', label: 'Not very active' },
                  { value: 'Some daily activity', label: 'Some daily activity' },
                  { value: 'Mostly active', label: 'Mostly active' },
                ].map((opt) => (
                  <div className="col-4 d-flex" key={opt.value}>
                    <button
                      type="button"
                      className={`assessment-option-btn w-100 ${
                        formData.dailyActivity === opt.value ? 'selected' : ''
                      }`}
                      onClick={() => handleSelect('dailyActivity', opt.value)}
                    >
                      {opt.label}
                      {formData.dailyActivity === opt.value && (
                        <span className="assessment-option-check"> ✓</span>
                      )}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </>
        );

      /* ── Step 5: What Matters Most ── */
      case 5:
        return (
          <>
            <h4 className="assessment-title">What Matters Most to You</h4>
            <p className="text-muted" style={{ fontSize: 14, marginBottom: 20 }}>
              This helps us tailor the support to your needs.
            </p>

            <div className="mb-3">
              <label className="form-label fw-semibold">
                What would you most like to improve right now?
              </label>
              <textarea
                className={`form-control ${errors.improvementGoal ? 'is-invalid' : ''}`}
                name="improvementGoal"
                placeholder="Tell us what matters most to you at this stage, or what you'd like to change. You can keep this brief."
                rows={3}
                value={formData.improvementGoal}
                onChange={handleChange}
              />
              {errors.improvementGoal && (
                <div className="invalid-feedback">{errors.improvementGoal}</div>
              )}
            </div>

            <div className="mb-3">
              <label className="form-label fw-semibold">
                What have you been told about what to expect going forward?{' '}
                <span className="text-muted fw-normal">(optional)</span>
              </label>
              <textarea
                className="form-control"
                name="expectationsFromDoctor"
                placeholder="Anything your doctor or care team has shared about outcomes or next steps"
                rows={2}
                value={formData.expectationsFromDoctor}
                onChange={handleChange}
              />
            </div>

            <div className="mb-3">
              <label className="form-label fw-semibold">
                What are you personally hoping for or aiming to achieve?
              </label>
              <textarea
                className={`form-control ${errors.personalHopes ? 'is-invalid' : ''}`}
                name="personalHopes"
                placeholder="Share what a meaningful improvement would look like for you"
                rows={2}
                value={formData.personalHopes}
                onChange={handleChange}
              />
              {errors.personalHopes && (
                <div className="invalid-feedback">{errors.personalHopes}</div>
              )}
            </div>
            <small className="text-muted">There are no right or wrong answers</small>
          </>
        );

      /* ── Step 6: Final Step ── */
      case 6:
        return (
          <>
            <h4 className="assessment-title">Final Step</h4>
            <p className="text-muted" style={{ fontSize: 14, marginBottom: 20 }}>
              This is the final step before we review your application for the recovery program.
            </p>

            <div className="mb-4">
              <label className="form-label fw-semibold">
                Is there anything else you'd like us to understand as we review your application?
              </label>
              <textarea
                className="form-control"
                name="additionalNotes"
                placeholder="Anything you feel is important for us to consider"
                rows={4}
                value={formData.additionalNotes}
                onChange={handleChange}
              />
            </div>

            <div className="assessment-confidential-box">
              <div style={{ fontSize: 20, marginBottom: 8 }}>🔒</div>
              <p>
                Each program is intentionally limited to ensure a highly personalized and focused
                experience.
              </p>
              <p style={{ marginBottom: 0 }}>
                All information is handled with complete confidentiality.
              </p>
            </div>
          </>
        );

      /* ── Step 7: Success ── */
      case 7:
        return (
          <div className="assessment-success">
            <div className="assessment-success-icon">✓</div>
            <h3>Request Received</h3>
            <p>Thank you for taking the time to share your situation with us.</p>
            <p>
              We will review your information with care, with the aim of understanding what would be
              most meaningful and appropriate for you.
            </p>
            <p>
              If the program could be a good fit, we will reach out to arrange a private
              consultation.
            </p>
            <p>
              <em>All information is handled with complete confidentiality.</em>
            </p>
            <button className="assessment-nav-btn mt-3" onClick={resetAndClose}>
              Close
            </button>
          </div>
        );

      default:
        return null;
    }
  };

  /* ── Render ───────────────────────────────────────────── */

  return (
    <Modal
      {...props}
      onHide={handleClose}
      size="lg"
      centered
      className="assessment-modal"
      backdrop={step > 0 && step < 7 ? 'static' : true}
    >
      <Modal.Header closeButton={!showCloseConfirm}>
        {step >= 1 && step <= TOTAL_STEPS && renderProgress()}
      </Modal.Header>

      <Modal.Body style={{ position: 'relative', padding: '24px 36px' }}>
        {/* Close confirmation overlay */}
        {showCloseConfirm && (
          <div className="assessment-close-confirm">
            <div style={{ fontSize: 32, marginBottom: 12 }}>⚠️</div>
            <h5>Are you sure you want to leave?</h5>
            <p>
              Your progress will not be saved. All information you've entered will be lost.
            </p>
            <div className="d-flex gap-3 justify-content-center">
              <button
                className="btn btn-outline-secondary px-4"
                onClick={() => setShowCloseConfirm(false)}
              >
                Continue Filling
              </button>
              <button className="btn btn-danger px-4" onClick={resetAndClose}>
                Yes, Leave
              </button>
            </div>
          </div>
        )}

        {renderStep()}
        <ToastContainer position="bottom-center" />
      </Modal.Body>

      {/* Footer with Back / Next / Submit */}
      {step >= 1 && step <= TOTAL_STEPS && !showCloseConfirm && (
        <Modal.Footer className="d-flex justify-content-between border-top pt-3">
          <button className="assessment-back-btn" onClick={handleBack}>
            ← Back
          </button>
          {step < TOTAL_STEPS ? (
            <button className="assessment-nav-btn" onClick={handleNext}>
              Continue
            </button>
          ) : (
            <button
              className="assessment-nav-btn"
              onClick={handleSubmit}
              disabled={submitting}
            >
              {submitting ? 'Submitting…' : 'Request Consideration'}
            </button>
          )}
        </Modal.Footer>
      )}
    </Modal>
  );
}
